/* eslint-disable @typescript-eslint/member-ordering */
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Injectable, InjectionToken } from '@angular/core';
import {
  FindingVersionRef,
  Roi,
  unlabeled,
  RoiFinding,
  RoiFindingMapper,
  RoiFindingProjectionService,
  generateRoiFindingKey,
} from '@analysis/domain';
import { type RoiRepository } from '@analysis/use-cases';

export interface RoiState {
  rois: EntityState<Roi>; // TODO: set limit and evict
  roiFindings: Map<string, RoiFinding>; // Key = "analystId:assetId:stepId"
  labelIndex: Map<string, Set<string>>; // label -> Set of roiIds
  assetIndex: Map<string, Set<string>>; // asset -> Set of roiIds
  loading: boolean;
  error: string | undefined;
  focusedRoi: string;
  /**
   * Rows a save from this page removed. Absence from `rois` cannot say this on its own -
   * it reads the same as a row never loaded - so a lagging page read would put them back.
   */
  deletedByOwnSave: Set<string>;
}

export const ROI_REPOSITORY = new InjectionToken<RoiRepository>('ROI_REPOSITORY');

@Injectable()
export class RoiStore extends ComponentStore<RoiState> {
  // #region Internal

  private readonly adapter: EntityAdapter<Roi>;

  // #endregion

  // #region Constructor

  constructor() {
    const adapter = createEntityAdapter<Roi>();
    super({
      rois: adapter.getInitialState(),
      roiFindings: new Map(),
      labelIndex: new Map(),
      assetIndex: new Map(),
      loading: false,
      error: undefined,
      focusedRoi: undefined,
      deletedByOwnSave: new Set(),
    });

    this.adapter = adapter;
  }

  // #endregion

  // #region API Properties

  public readonly rois$ = this.select((state) => state.rois.entities);
  public readonly assetIndex$ = this.select((state) => state.assetIndex);
  public readonly labelIndex$ = this.select((state) => state.labelIndex);
  public readonly roisLoading$ = this.select((state) => state.loading);

  public readonly focusedRoiId$ = this.select((state) => state.focusedRoi);
  public readonly focusedRoi$ = this.select((state) => state.rois.entities[state.focusedRoi]);

  public readonly error$ = this.select((state) => state.error);

  public readonly roiFindings$ = this.select((state) => state.roiFindings);

  // #endregion

  // #region API Methods

  public readonly setFocusedRoi = this.updater((state, roiId: string) => ({
    ...state,
    focusedRoi: roiId,
  }));

  public readonly clearFocusedRoi = this.updater((state) => ({
    ...state,
    focusedRoi: undefined,
  }));

  public readonly clearRois = this.updater((state) => ({
    ...state,
    rois: this.adapter.getInitialState(),
    roiFindings: new Map(),
    labelIndex: new Map(),
    assetIndex: new Map(),

    focusedRoi: undefined,
    // Those ids belong to the generation being left; nothing in the next one can match them.
    deletedByOwnSave: new Set<string>(),
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    error,
  }));

  public readonly clearError = this.updater((state) => ({
    ...state,
    error: undefined,
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: undefined,
  }));

  public readonly setLoadError = this.updater((state, error: string) => ({
    ...state,
    error,
    loading: false,
  }));

  public readonly initializeLabelIndex = this.updater((state, labelIds: string[]) => {
    const newLabelIndex = new Map<string, Set<string>>();
    for (const id of labelIds) newLabelIndex.set(id, new Set());
    return {
      ...state,
      rois: this.adapter.getInitialState(),
      roiFindings: new Map(),
      labelIndex: newLabelIndex,
      assetIndex: new Map(),
    };
  });

  public readonly loadBlobUrl = this.updater((state, payload: { roiId: string; url: string }) => ({
    ...state,
    rois: this.adapter.updateOne({ id: payload.roiId, changes: { url: payload.url } }, state.rois),
  }));

  public readonly completeSyncWithUpdatedRois = this.updater((state, updatedRois: Roi[]) => ({
    ...state,
    roiFindings: this.updateRoiFindingsAfterSync(state.roiFindings, updatedRois),
  }));

  public readonly completeSyncWithSave = this.updater(
    (state, payload: { roisToUpsert: Roi[]; deletedRois: string[]; allUpdatedRois: Roi[] }) =>
      this.completeSync(state, payload.roisToUpsert, payload.deletedRois, payload.allUpdatedRois)
  );

  public readonly createRoiWithFindings = this.updater(
    (state, payload: { roiFindings: RoiFinding[]; updatedRois: Map<string, Roi> }) => {
      const roiArray = Array.from(payload.updatedRois.values());
      const newRoiFindings = new Map(state.roiFindings);
      payload.roiFindings.forEach((roiFinding) => {
        const key = generateRoiFindingKey(roiFinding.creatorId, roiFinding.assetId, roiFinding.pipelineStepId);
        newRoiFindings.set(key, roiFinding);
      });
      return {
        ...this.indexAndUpsert(state, roiArray),
        roiFindings: newRoiFindings,
        error: undefined,
      };
    }
  );

  public readonly loadAssetRoisResult = this.updater(
    (state, payload: { rois: Roi[]; roiFindings: Map<string, RoiFinding> }) => ({
      ...this.initWithRois(state, payload.rois),
      roiFindings: new Map([...state.roiFindings, ...payload.roiFindings]),
      loading: false,
      error: undefined,
    })
  );

  public readonly mergeMosaicBatch = this.updater(
    (
      state,
      payload: {
        rois: Roi[];
        changedRoiIds: Set<string>;
        /** findingId -> the version a save from this page wrote. */
        ownSaves: Map<string, FindingVersionRef>;
      }
    ) => {
      const processed: Roi[] = [];

      for (const roi of payload.rois) {
        const existing = state.rois.entities[roi.id];

        if (this.supersededLocally(state, roi, payload.ownSaves)) continue;

        // Only merge labels from existing for UNCHANGED server ROIs.
        // Changed ROIs arrive already projected — merging would undo the projection.
        if (existing && !payload.changedRoiIds.has(roi.id)) {
          const merged = new Map(existing.labels);
          roi.labels.forEach((score, label) => merged.set(label, score));
          processed.push({ ...roi, labels: merged });
          continue;
        }

        processed.push(roi);
      }

      return {
        ...this.indexAndUpsert(state, processed),
        loading: false,
        error: undefined,
      };
    }
  );

  // #endregion

  // #region Reducer Functions

  private initWithRois(state: RoiState, rois: Roi[]) {
    const clearedLabelIndex = new Map<string, Set<string>>();
    for (const label of state.labelIndex.keys()) clearedLabelIndex.set(label, new Set());

    return this.indexAndUpsert(
      {
        ...state,
        rois: this.adapter.getInitialState(),
        labelIndex: clearedLabelIndex,
        assetIndex: new Map(),
      },
      rois
    );
  }

  public readonly upsertRois = this.updater((state, rois: Map<string, Roi>): RoiState => {
    return this.indexAndUpsert(state, Array.from(rois.values()));
  });

  /**
   * Rows read from the Mosaic projection rather than written locally: a detail read, or the
   * hydration of a changed roi. The projection trails Parse, so these can predate a save made
   * from this page, and `upsertRois` would overwrite the newer local copy with the older one.
   */
  public readonly upsertProjectedRois = this.updater(
    (state, payload: { rois: Map<string, Roi>; ownSaves: Map<string, FindingVersionRef> }): RoiState =>
      this.indexAndUpsert(
        state,
        Array.from(payload.rois.values()).filter((roi) => !this.supersededLocally(state, roi, payload.ownSaves))
      )
  );

  public readonly removeRoisByIds = this.updater((state, ids: string[]): RoiState =>
    ids.length > 0 ? this.removeRois(state, ids) : state
  );

  /**
   * Whether a row the projection answered with must not reach the store, because this view can
   * already do better than it.
   *
   * Two reasons, and the second needs the first as its complement: a row a save from here
   * deleted is absent, which on its own reads exactly like a row never loaded; and a row from
   * before such a save is only worth dropping when the store holds the copy that save
   * produced. Anything else is merged - a row with no local counterpart is better shown
   * slightly stale than not at all, since the page token moves on regardless.
   */
  private supersededLocally(state: RoiState, roi: Roi, ownSaves: Map<string, FindingVersionRef>): boolean {
    if (state.deletedByOwnSave.has(roi.id)) return true;

    return !!state.rois.entities[roi.id] && RoiFindingProjectionService.supersededByOwnSave(roi, ownSaves);
  }

  private indexAndUpsert(state: RoiState, rois: Roi[]): RoiState {
    const newLabelIndex = new Map(state.labelIndex);
    const newAssetIndex = new Map(state.assetIndex);
    const mergedRois: Roi[] = [];

    for (const roi of rois) {
      const existing = state.rois.entities[roi.id];

      // Preserve display fields (uuid, cropFile, url) from existing entry
      // when the incoming ROI doesn't have them (e.g. from Parse via getById).
      // additionalLabelsIncluded is sticky-true: once a ROI has been fully
      // enriched, a partial label-paged response must not demote it.
      const merged = existing
        ? {
            ...roi,
            uuid: roi.uuid ?? existing.uuid,
            cropFile: roi.cropFile ?? existing.cropFile,
            url: roi.url ?? existing.url,
            additionalLabelsIncluded: existing.additionalLabelsIncluded || roi.additionalLabelsIncluded,
          }
        : roi;
      mergedRois.push(merged);

      // Update label index — only touch labels that actually changed
      // to preserve Set insertion order (cells use positional access)
      const oldLabelKeys = existing
        ? existing.labels.size > 0
          ? new Set(existing.labels.keys())
          : new Set([unlabeled])
        : new Set<string>();
      const newLabelKeys = roi.labels.size > 0 ? new Set(roi.labels.keys()) : new Set([unlabeled]);

      for (const label of oldLabelKeys) {
        if (!newLabelKeys.has(label)) {
          newLabelIndex.get(label)?.delete(roi.id);
        }
      }

      for (const label of newLabelKeys) {
        if (!oldLabelKeys.has(label)) {
          const set = newLabelIndex.get(label) ?? new Set();
          set.add(roi.id);
          newLabelIndex.set(label, set);
        }
      }

      // Asset index
      const assetSet = newAssetIndex.get(roi.assetId) ?? new Set();
      assetSet.add(roi.id);
      newAssetIndex.set(roi.assetId, assetSet);
    }

    return {
      ...state,
      rois: this.adapter.upsertMany(mergedRois, state.rois),
      labelIndex: newLabelIndex,
      assetIndex: newAssetIndex,
    };
  }

  private removeRois(state: RoiState, ids: string[]): RoiState {
    const newLabelIndex = new Map(state.labelIndex);
    const newAssetIndex = new Map(state.assetIndex);

    for (const id of ids) {
      const roi = state.rois.entities[id];
      if (!roi) continue;

      const roisForAsset = newAssetIndex.get(roi.assetId) ?? new Set();
      roisForAsset.delete(id);
      newAssetIndex.set(roi.assetId, roisForAsset);

      const labelKeys = roi.labels.size > 0 ? roi.labels.keys() : [unlabeled];
      for (const label of labelKeys) {
        newLabelIndex.get(label)?.delete(id);
      }
    }

    return {
      ...state,
      rois: this.adapter.removeMany(ids, state.rois),
      labelIndex: newLabelIndex,
      assetIndex: newAssetIndex,
    };
  }

  private completeSync(state: RoiState, roisToUpsert: Roi[], idsToDelete: string[], allUpdatedRois: Roi[]): RoiState {
    const upserted = { ...state, rois: this.adapter.upsertMany(roisToUpsert, state.rois) };
    const removed = this.removeRois(upserted, idsToDelete);
    return {
      ...removed,
      roiFindings: this.updateRoiFindingsAfterSync(removed.roiFindings, allUpdatedRois),
      // Remembered because the projection can still answer with these rows for a while, and
      // by then nothing else distinguishes them from rows this view has simply not loaded.
      deletedByOwnSave: new Set([...state.deletedByOwnSave, ...idsToDelete]),
    };
  }

  private updateRoiFindingsAfterSync(
    existingRoiFindings: Map<string, RoiFinding>,
    updatedRois: Roi[]
  ): Map<string, RoiFinding> {
    const newRoiFindings = new Map(existingRoiFindings);

    const newFindingsFromRois = RoiFindingMapper.fromRois(updatedRois);

    const findingIdsToReplace = new Set<string>();
    for (const finding of newFindingsFromRois.values()) findingIdsToReplace.add(finding.findingId);

    // Remove old RoiFindings that have the same uuid as the new ones
    for (const [key, roiFinding] of newRoiFindings.entries()) {
      if (findingIdsToReplace.has(roiFinding.findingId)) {
        newRoiFindings.delete(key);
      }
    }

    // Add the new RoiFindings
    for (const [key, roiFinding] of newFindingsFromRois.entries()) {
      newRoiFindings.set(key, roiFinding);
    }

    return newRoiFindings;
  }

  // #endregion
}
